import { ACTION, SideEnum, isSpot, useSecuritiesContext, type Security } from '@talos/kyoko';
import {
  AvaAIPrimeOrderFormErrorCode,
  primeOrderFormInputSchema,
  primeOrderFormToolContract,
  type PrimeOrderFormInput,
  type PrimeOrderFormResult,
} from '@talos/shared/avaAI';
import { useMemo, useRef } from 'react';

import { orderSliceActions } from '@src/components/OMS/NewOrder/OrderSlice';
import { OMSSliceActions } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { AppThunkDispatch } from '@src/providers/AppStateProvider/types';
import type { ClientToolModule } from '../clientToolExecutor';
import { createAvaAIClientToolHandler } from '../clientToolHandler';

export type { PrimeOrderFormInput, PrimeOrderFormResult } from '@talos/shared/avaAI';

interface PrimeOrderFormDependencies {
  dispatch: AppThunkDispatch;
  activeSecuritiesBySymbol: ReadonlyMap<string, Security>;
}

function parseInput(value: unknown): PrimeOrderFormInput | undefined {
  const parsed = primeOrderFormInputSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

function errorResult(
  code: (typeof AvaAIPrimeOrderFormErrorCode)[keyof typeof AvaAIPrimeOrderFormErrorCode],
  message: string
): PrimeOrderFormResult {
  return { status: 'error', code, message };
}

/** Executes the tool against the latest Ava state after validating the untrusted model input. */
export async function executePrimeOrderForm(
  value: unknown,
  { dispatch, activeSecuritiesBySymbol }: PrimeOrderFormDependencies
): Promise<PrimeOrderFormResult> {
  const input = parseInput(value);
  if (input == null) {
    return errorResult(
      AvaAIPrimeOrderFormErrorCode.InvalidInput,
      'The order form tool requires a symbol, buy or sell side, and positive decimal quantity.'
    );
  }

  const security = activeSecuritiesBySymbol.get(input.symbol);
  if (security == null) {
    return errorResult(
      AvaAIPrimeOrderFormErrorCode.SymbolNotAvailable,
      `The symbol ${input.symbol} is not available for trading.`
    );
  }
  if (!isSpot(security) || security.BaseCurrency == null) {
    return errorResult(
      AvaAIPrimeOrderFormErrorCode.UnsupportedProduct,
      `The symbol ${input.symbol} is not a supported spot instrument.`
    );
  }

  try {
    dispatch(OMSSliceActions.openView(OMSView.NewOrderForm));
    await dispatch(
      orderSliceActions.primeNewOrderForm({
        symbol: security.Symbol,
        side: input.side === 'buy' ? SideEnum.Buy : SideEnum.Sell,
        orderQty: input.quantity,
        currency: security.BaseCurrency,
        forceFullPrime: true,
        ignoreCounterCurrencyDefault: true,
      })
    ).unwrap();
  } catch {
    return errorResult(AvaAIPrimeOrderFormErrorCode.PrimeFailed, 'The order form could not be primed.');
  }

  return { status: 'success', order: input };
}

export function createPrimeOrderFormClientTool(
  getDependencies: () => PrimeOrderFormDependencies
): ClientToolModule<typeof primeOrderFormToolContract.name, PrimeOrderFormResult> {
  return {
    name: primeOrderFormToolContract.name,
    outputSchema: primeOrderFormToolContract.outputSchema,
    execute: value => executePrimeOrderForm(value, getDependencies()),
    invocationPolicy: {
      maxAttemptsPerUserTurn: 1,
      limitResult: errorResult(
        AvaAIPrimeOrderFormErrorCode.MultiplePrimesNotSupported,
        'Only one order form prime is supported per request.'
      ),
    },
  };
}

export function usePrimeOrderFormClientTool() {
  const dispatch = useAppStateDispatch();
  const { activeSecuritiesBySymbol } = useSecuritiesContext();
  const { isAuthorized } = useRoleAuth();
  const isAvailable = isAuthorized(ACTION.VIEW_ORDER_FORM);
  const dependenciesRef = useRef<PrimeOrderFormDependencies>({ dispatch, activeSecuritiesBySymbol });
  dependenciesRef.current = { dispatch, activeSecuritiesBySymbol };

  return useMemo(
    () =>
      createAvaAIClientToolHandler({
        ...createPrimeOrderFormClientTool(() => dependenciesRef.current),
        isAvailable,
      }),
    [isAvailable]
  );
}
